import { toast } from '@acast/decibel';
import * as Sentry from '@sentry/browser';
import { clearChanges, selectChanges, selectHasChanges } from 'ducks/changes';
import { fetchShowFeatureFlags } from 'ducks/feature-flags';
import { fetchShow, selectShow, updateShow } from 'ducks/shows';
import T from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { mergeShowChanges } from 'routes/show/util';
import { presentModal } from 'services/modals';
import ContentContainer from 'shared/components/content-container';
import Modal from 'shared/components/modal';
import PendingPage from 'shared/components/pending-page';
import navigateTo from 'shared/helpers/navigate-to';
import Navigation from './Navigation';

class ShowPage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      nextLocation: null,
    };
  }

  componentDidMount() {
    const { params, route } = this.props;
    const { router } = this.context;

    router.setRouteLeaveHook(route, this.routerWillLeave);
    this.props.fetchShow(params.showId);
    this.props.fetchShowFeatureFlags(params.showId);
  }

  componentDidUpdate(prevProps) {
    const { nextLocation } = this.state;
    const { hasChanges } = this.props;

    if (nextLocation && !hasChanges && prevProps.hasChanges) {
      navigateTo(nextLocation);
    }

    if (hasChanges && !prevProps.hasChanges && this.saveToastId === null) {
      this.saveToastId = toast.secondary('You have modified the show settings', {
        multiLine: true,
        position: 'bottom',
        actions: [
          { text: 'Save', onClick: () => this.saveChanges() },
          { text: 'Discard Changes', onClick: () => this.clearChanges() },
        ],
      });
    }

    if (!hasChanges && prevProps.hasChanges && this.saveToastId !== null) {
      toast.close(this.saveToastId);
      this.saveToastId = null;
    }

    if (prevProps.params.showId !== this.props.params.showId) {
      this.props.fetchShowFeatureFlags(this.props.params.showId);
    }
  }

  saveToastId = null;

  clearChanges() {
    const {
      // eslint-disable-next-line no-shadow
      clearChanges,
      params: { showId },
    } = this.props;
    clearChanges(showId);
  }

  saveChanges() {
    // eslint-disable-next-line no-shadow
    const { show, changes, clearChanges, updateShow } = this.props;
    const showWithChanges = mergeShowChanges(show, changes);

    return updateShow(showWithChanges)
      .then(() => clearChanges(showWithChanges.id))
      .catch(error => {
        toast.warning(`Error saving show settings: ${error.message}`, { closable: true });
        Sentry.captureException(error);
      });
  }

  confirmNavigate(nextLocation) {
    presentModal(
      <Modal
        title="Discard changes?"
        dialog="You have unsaved changes on this show. Do you wish to navigate away from this page and discard your changes?"
        actions={[
          { name: 'No, Stay on Page', value: false, variant: 'tertiary' },
          {
            name: 'Leave & Discard Changes',
            value: true,
            variant: 'primary',
            intent: 'danger',
          },
        ]}
        onDismiss={result => {
          if (result) {
            this.setState({ nextLocation });
            this.props.clearChanges(this.props.params.showId);
          }
        }}
      />
    );
  }

  routerWillLeave = nextLocation => {
    if (this.props.hasChanges) {
      this.confirmNavigate(nextLocation);
      return false;
    }

    return undefined;
  };

  render() {
    const { children, show } = this.props;

    if (!show) {
      return <PendingPage />;
    }

    return (
      <>
        <Navigation selected />
        <ContentContainer>{children}</ContentContainer>
      </>
    );
  }
}

ShowPage.contextTypes = {
  router: T.object.isRequired,
};

ShowPage.propTypes = {
  children: T.node,
  clearChanges: T.func.isRequired,
  hasChanges: T.bool,
  fetchShow: T.func.isRequired,
  fetchShowFeatureFlags: T.func.isRequired,
  location: T.shape({
    pathname: T.string.isRequired,
  }).isRequired,
  params: T.shape({
    showId: T.string.isRequired,
  }).isRequired,
  show: T.object,
  route: T.object.isRequired,
  updateShow: T.func.isRequired,
  changes: T.object,
};

export default connect(
  (state, { params }) => ({
    changes: selectChanges(params.showId)(state),
    hasChanges: selectHasChanges(params.showId)(state),
    show: selectShow(params.showId)(state),
  }),
  { clearChanges, fetchShowFeatureFlags, fetchShow, updateShow }
)(ShowPage);
